import { Controller } from '@hotwired/stimulus'
import { useResize } from 'stimulus-use'

// Connects to data-controller="tabs"
export default class extends Controller {
  static targets = ['tab', 'slider']
  static values = {
    currentAttribute: { type: String, default: 'aria-selected' },
    sliderAttribute: { type: String, default: 'data-slider' },
    tabNameAttribute: { type: String, default: 'data-tabs-name' },
    // Slide the panel horizontally on tab change (View Transitions API) — the server
    // enables it for the native app only.
    slide: { type: Boolean, default: false },
  }

  connect() {
    if (this.slideValue) {
      // Rendered content of already-visited panels, keyed by tab href: revisits slide
      // in the KNOWN content instantly at click, the fetch refreshing it in place.
      this.panelCache = new Map()
      this.slideFrameRender = this.#slideFrameRender.bind(this)
      this.element.addEventListener('turbo:before-frame-render', this.slideFrameRender)
      // The panel slide (pseudo-elements on :root, see animations.css) must tick on
      // the SAME clock AND curve as the tabs: mirror the nav's knobs onto the root.
      document.documentElement.style.setProperty('--tabs-speed', `${this.#transitionDuration()}ms`)
      document.documentElement.style.setProperty('--tabs-ease', getComputedStyle(this.sliderTarget).transitionTimingFunction)
    }

    if (!this.hasSliderTarget) return

    this.#refresh()
    this.sliderTarget.setAttribute(this.sliderAttributeValue, '')
    useResize(this)
    // Préchargement natif : connect() tourne dans une webview hors écran à la
    // géométrie provisoire — re-mesure (en place) à chaque apparition réelle.
    this.nativeAppeared = () => this.#refreshWhenSettled()
    window.addEventListener('native:appeared', this.nativeAppeared)
    // Poppins may load after connect, changing the label widths.
    document.fonts?.ready?.then(() => {
      this.#refreshWhenSettled()

      // Transition classes are gated on this attribute: without it, the boot
      // animates its own corrections (CSS fallback width → measured width).
      // The offsetWidth read is load-bearing — it flushes the measured
      // variables into computed style before arming; otherwise both land in
      // the same recalc and the jump animates anyway.
      void this.element.offsetWidth
      this.element.setAttribute('data-tabs-settled', '')
    })
  }

  disconnect() {
    clearTimeout(this.resizeTimer)
    if (this.nativeAppeared) {
      window.removeEventListener('native:appeared', this.nativeAppeared)
    }
    if (this.slideFrameRender) {
      this.element.removeEventListener('turbo:before-frame-render', this.slideFrameRender)
    }
  }

  resize() {
    this.#refreshWhenSettled()
  }

  select(event) {
    const previousTab = this.#activeTab()

    // The slide ALWAYS starts at click time — same instant as the slider/fold
    // animations, perfect sync. Already-visited panels slide their cached content in;
    // a first visit slides an empty panel, the content fading in when the fetch
    // renders (#slideFrameRender). Either way the fetch proceeds and refreshes.
    const from = this.tabTargets.indexOf(previousTab)
    const to = this.tabTargets.indexOf(event.currentTarget)
    const direction = from >= 0 && to >= 0 && from !== to ? (to > from ? 'forward' : 'back') : null
    if (this.slideValue && direction && document.startViewTransition) {
      this.#cacheCurrentPanel(previousTab)
      const cached = this.panelCache.get(event.currentTarget.getAttribute('href'))
      this.contentPending = !cached
      this.#slideInPanel(direction, cached ? cached.map((node) => node.cloneNode(true)) : [])
    }

    for (const tab of this.tabTargets) {
      tab.setAttribute(this.currentAttributeValue, false)
    }
    event.currentTarget.setAttribute(this.currentAttributeValue, true)
    const currentName = event.currentTarget.getAttribute(this.tabNameAttributeValue)
    if (currentName) this.data.set('current', currentName)

    this.element.querySelectorAll('details[open]').forEach((d) => d.removeAttribute('open'))

    if (!this.hasSliderTarget) return
    // Measurements are wrong while the tabs transition — see #refreshWhenSettled.
    this.settleUntil = performance.now() + this.#transitionDuration() + 30

    this.#moveSliderToActiveTab(previousTab)
  }

  #cacheCurrentPanel(previousTab) {
    const frame = this.element.querySelector('#profile-panel')
    const key = previousTab?.getAttribute('href')
    if (!frame || !key) return
    this.panelCache.set(key, [...frame.children].map((node) => node.cloneNode(true)))
  }

  // Slides the given nodes in as the panel content, at click time.
  #slideInPanel(direction, nodes) {
    const frame = this.element.querySelector('#profile-panel')
    if (!frame) return

    // BEFORE the old-state capture: a tab switch starts at the top (Twitter-style).
    // Captured while scrolled, the shorter incoming panel clamps the scroll and the
    // transition animates that vertical delta on top of the slide (upward drift).
    window.scrollTo({ top: 0, behavior: 'instant' })

    document.documentElement.dataset.panelSlide = direction
    this.activeSlide = document.startViewTransition(() => {
      frame.innerHTML = ''
      frame.append(...nodes)
    })
    this.activeSlide.finished.finally(() => {
      delete document.documentElement.dataset.panelSlide
      this.activeSlide = null
    })
  }

  // Frame render (fetch completed). The slide already ran at click time; the fresh
  // content lands in place IMMEDIATELY — even mid-slide (::view-transition-new is a
  // live rendering of the panel, so the content shows up inside the sliding panel).
  // Over cached content the swap is invisible (identical DOM in the common case);
  // over an empty first-visit panel it fades in — short and fixed: it signals content
  // arrival, it is not part of the tab gesture, so it does not share its clock.
  #slideFrameRender(event) {
    if (event.target.id !== 'profile-panel' || !this.contentPending) return
    this.contentPending = false

    const originalRender = event.detail.render
    event.detail.render = (currentElement, newElement) => {
      originalRender(currentElement, newElement)
      event.target.animate({ opacity: [0, 1] }, { duration: 150, easing: 'ease-out' })
    }
  }

  closeDetails(event) {
    this.element.querySelectorAll('details[open]').forEach((details) => {
      if (!details.parentElement.contains(event.target)) details.removeAttribute('open')
    })
  }

  #activeTab() {
    return this.tabTargets.find((tab) => tab.getAttribute(this.currentAttributeValue) == 'true')
  }

  // Tab transition duration (ms), read off the slider (duration-(--tabs-speed)).
  #transitionDuration() {
    return (parseFloat(getComputedStyle(this.sliderTarget).transitionDuration) || 0.3) * 1000
  }

  #refresh() {
    this.#applyExpandedWidths()
    this.#moveSliderToActiveTab()
  }

  // useResize also fires on panel-content resizes, i.e. right after a tab click:
  // measuring mid-fold would overwrite the final geometry computed by select, so
  // defer to the end of the transition window.
  #refreshWhenSettled() {
    const wait = (this.settleUntil || 0) - performance.now()
    clearTimeout(this.resizeTimer)
    if (wait > 0) {
      this.resizeTimer = setTimeout(() => this.#refreshInPlace(), wait + 50)
    } else {
      this.#refreshInPlace()
    }
  }

  // Correction de géométrie HORS interaction (resize, apparition d'une webview
  // préchargée) : peinte en place — transitions désarmées le temps du recalcul,
  // flush de style, ré-armement (la danse du boot, voir connect()). Sans ça, la
  // première apparition d'un onglet préchargé animait la correction (le
  // « gigotement » que data-tabs-settled avait éliminé au chargement).
  #refreshInPlace() {
    const wasSettled = this.element.hasAttribute('data-tabs-settled')
    if (wasSettled) this.element.removeAttribute('data-tabs-settled')
    this.#refresh()
    if (wasSettled) {
      void this.element.offsetWidth
      this.element.setAttribute('data-tabs-settled', '')
    }
  }

  // Expanded width of each mobile tab = collapsed slot + label padding + measured text
  // width, so the icon offset is identical in both states (it never moves during the
  // fold). Written to the CSS vars the view's width classes consume.
  #applyExpandedWidths() {
    this.expandedWidths = new Map()

    const collapsedTab = this.tabTargets.find(
      (tab) => tab.getAttribute(this.currentAttributeValue) != 'true'
    )
    const activeLabel = this.#activeTab()?.querySelector('[data-tabs-label]')
    if (!collapsedTab || !activeLabel) return

    const collapsedWidth = collapsedTab.offsetWidth
    const labelGap = parseFloat(getComputedStyle(activeLabel).paddingLeft) || 0

    for (const tab of this.tabTargets) {
      const label = tab.querySelector('[data-tabs-label]')
      if (!label) continue

      // Subtract the state-dependent padding-left: keep only the text width.
      const textWidth = Math.ceil(
        label.scrollWidth - (parseFloat(getComputedStyle(label).paddingLeft) || 0)
      )
      const width = collapsedWidth + Math.ceil(labelGap) + textWidth
      // --tab-label-w must be the exact expanded label width (padding included,
      // border-box): a looser cap desyncs label and slot mid-transition (icon wiggle).
      tab.style.setProperty('--tab-expanded-w', `${width}px`)
      tab.style.setProperty('--tab-label-w', `${Math.ceil(labelGap) + textWidth}px`)
      this.expandedWidths.set(tab, width)
    }
  }

  // Sets left/width once; the slide itself is the slider's CSS transition.
  // On click, measurements reflect the PRE-transition layout, so the final geometry is
  // computed: shift by the previous tab's fold (when it sits before the target) plus
  // the justify-between gap redistribution.
  #moveSliderToActiveTab(previousTab = null) {
    const activeTab = this.#activeTab()
    if (!activeTab) return

    let left = activeTab.offsetLeft

    const expandedWidth = this.expandedWidths?.get(activeTab)
    const slotsAnimate = expandedWidth > 0 && !window.matchMedia('(min-width: 768px)').matches

    // offsetWidth can be read mid-transition; the stored constant is the arrival width.
    let width = slotsAnimate ? expandedWidth : activeTab.offsetWidth

    if (slotsAnimate && previousTab && previousTab !== activeTab) {
      const collapsedWidth = activeTab.offsetWidth // target tab is still collapsed
      const previousWidth = previousTab.offsetWidth // previous tab is still expanded
      const index = this.tabTargets.indexOf(activeTab)
      const gapCount = this.tabTargets.length - 1

      if (this.tabTargets.indexOf(previousTab) < index) left -= previousWidth - collapsedWidth
      left += index * ((previousWidth - expandedWidth) / gapCount)
    }

    this.sliderTarget.style.left = `${left}px`
    this.sliderTarget.style.width = `${width}px`
  }
}
